import React, { createContext, useContext, useState, useEffect } from 'react';
import { getRestaurantTheme, getAvailableThemes } from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const getDefaultTheme = () => ({
  id: 'classic',
  name: 'Classic Elegance',
  colors: {
    primary: '#2c3e50',
    secondary: '#e74c3c',
    accent: '#f39c12',
    background: '#ffffff',
    text: '#2c3e50',
  },
  fonts: {
    family: '"Inter", sans-serif',
  },
});

const lightenColor = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, (num >> 8 & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
};

const darkenColor = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, (num >> 8 & 0x00FF) - amt);
  const B = Math.max(0, (num & 0x0000FF) - amt);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
};

const adjustOpacity = (color, opacity) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// WCAG contrast helpers — ensure per-restaurant brand colors stay readable
// on the cream surface. Customer Menu reads `--theme-primary-text` for any
// brand-colored TEXT/icon role; `--theme-primary` itself remains the fill.
const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
};

const relativeLuminance = ({ r, g, b }) => {
  const channel = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrastRatio = (hexA, hexB) => {
  const lA = relativeLuminance(hexToRgb(hexA));
  const lB = relativeLuminance(hexToRgb(hexB));
  const [hi, lo] = lA > lB ? [lA, lB] : [lB, lA];
  return (hi + 0.05) / (lo + 0.05);
};

// Cream surface used as the customer Menu background — keep in sync with
// --surface-secondary in styles/global.css.
const CREAM_BG = '#fffbf5';
const TARGET_CONTRAST = 4.5;

// If `color` fails AA against cream, walk it darker in 5% steps until it does.
const deriveAccessibleText = (color) => {
  let candidate = color;
  for (let step = 0; step < 16; step += 1) {
    if (contrastRatio(candidate, CREAM_BG) >= TARGET_CONTRAST) return candidate;
    candidate = darkenColor(candidate, 5);
  }
  return candidate;
};

function applyTheme(themeConfig) {
  if (!themeConfig?.colors) return;
  const root = document.documentElement;
  const { primary, secondary, accent, background, text } = themeConfig.colors;
  root.style.setProperty('--theme-primary', primary);
  root.style.setProperty('--theme-secondary', secondary);
  root.style.setProperty('--theme-accent', accent);
  root.style.setProperty('--theme-background', background);
  root.style.setProperty('--theme-text', text);
  root.style.setProperty('--theme-font-family', themeConfig.fonts?.family || '"Inter", sans-serif');
  root.style.setProperty('--theme-primary-light', lightenColor(primary, 20));
  root.style.setProperty('--theme-primary-dark', darkenColor(primary, 20));
  root.style.setProperty('--theme-text-secondary', adjustOpacity(text, 0.7));
  root.style.setProperty('--theme-text-tertiary', adjustOpacity(text, 0.5));
  // AA-safe variants for text/icon use of brand colors on cream surfaces.
  root.style.setProperty('--theme-primary-text', deriveAccessibleText(primary));
  root.style.setProperty('--theme-secondary-text', deriveAccessibleText(secondary));
  root.style.setProperty('--theme-accent-text', deriveAccessibleText(accent));
  // Translucent primary tints for tinted backgrounds (featured rails, hover wash, soft borders).
  root.style.setProperty('--theme-primary-tint', adjustOpacity(primary, 0.08));
  root.style.setProperty('--theme-primary-wash', adjustOpacity(primary, 0.02));
  root.style.setProperty('--theme-primary-edge', adjustOpacity(primary, 0.18));
  root.setAttribute('data-theme', themeConfig.id || 'classic');
}

export const ThemeProvider = ({ children, restaurantId, previewThemeId = null }) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId && !previewThemeId) {
      const defaultTheme = getDefaultTheme();
      setTheme(defaultTheme);
      applyTheme(defaultTheme);
      setLoading(false);
      return;
    }

    const fetchTheme = async () => {
      try {
        if (previewThemeId) {
          const res = await getAvailableThemes();
          const themes = res.data?.themes || [];
          const previewTheme = themes.find((t) => t.id === previewThemeId);
          if (previewTheme) {
            const themeObj = {
              id: previewTheme.id,
              name: previewTheme.name,
              colors: {
                primary: previewTheme.color_primary,
                secondary: previewTheme.color_secondary,
                accent: previewTheme.color_accent,
                background: previewTheme.color_background,
                text: previewTheme.color_text,
              },
              fonts: { family: previewTheme.font_family || '"Inter", sans-serif' },
            };
            setTheme(themeObj);
            applyTheme(themeObj);
          } else {
            const defaultTheme = getDefaultTheme();
            setTheme(defaultTheme);
            applyTheme(defaultTheme);
          }
        } else {
          const res = await getRestaurantTheme(restaurantId);
          const themeData = res.data?.theme;
          if (themeData) {
            setTheme(themeData);
            applyTheme(themeData);
          } else {
            const defaultTheme = getDefaultTheme();
            setTheme(defaultTheme);
            applyTheme(defaultTheme);
          }
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        const defaultTheme = getDefaultTheme();
        setTheme(defaultTheme);
        applyTheme(defaultTheme);
      } finally {
        setLoading(false);
      }
    };

    fetchTheme();
  }, [restaurantId, previewThemeId]);

  const updateTheme = (newTheme) => {
    if (newTheme) {
      setTheme(newTheme);
      applyTheme(newTheme);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, loading, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
